import { useEffect, useRef, useState } from "react";
import { Volume2 } from "lucide-react";
import api from "@/api";
import { useLanguage } from "@/i18n/index";

const TTS_LANG = {
  fr: "fr-FR",
  ar: "ar-MA",
};

export function speakPatientText(text, language = "fr") {
  if (!text || !("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = TTS_LANG[language] || TTS_LANG.fr;
  utterance.rate = 0.9;
  utterance.pitch = 1;
  window.speechSynthesis.speak(utterance);
}

export function usePatientAudioCache(extraKeys = []) {
  const { language } = useLanguage();
  const [audioCache, setAudioCache] = useState({});
  const extraKeysHash = extraKeys.join("|");

  useEffect(() => {
    let cancelled = false;
    async function loadAudios() {
      try {
        const res = await api.get(`/audios?language=${language}`, { skipAuthRedirect: true });
        if (cancelled) return;
        const cache = {};
        (res.data || []).forEach(audio => {
          cache[audio.audioKey] = audio;
        });
        setAudioCache(cache);
      } catch {
        if (!cancelled) setAudioCache({});
      }
    }
    loadAudios();
    return () => { cancelled = true; };
  }, [language, extraKeysHash]);

  return audioCache;
}

export function PatientSpeakerButton({ audioKey, audioCache, fallbackText, className = "", size = "default", testId }) {
  const { language, t } = useLanguage();
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const sizeClass = size === "sm" ? "h-9 w-9 p-2" : "h-11 w-11 p-3";

  const handleSpeak = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();

    const audioMeta = audioCache?.[audioKey];
    if (audioMeta?.id) {
      try {
        setPlaying(true);
        const cacheBust = audioMeta.updatedAt || audioMeta.createdAt || Date.now();
        const res = await api.get(`/audios/${audioMeta.id}/play?v=${encodeURIComponent(cacheBust)}`, {
          responseType: "blob",
          skipAuthRedirect: true,
        });
        const blobUrl = URL.createObjectURL(res.data);
        const audio = new Audio(blobUrl);
        audioRef.current = audio;
        const cleanup = () => {
          setPlaying(false);
          try { URL.revokeObjectURL(blobUrl); } catch { return; }
        };
        audio.onended = cleanup;
        audio.onerror = () => {
          cleanup();
          speakPatientText(fallbackText, language);
        };
        await audio.play();
        return;
      } catch {
        setPlaying(false);
      }
    }
    if (audioKey) {
      try {
        setPlaying(true);
        const staticUrl = `/audio/patient/${language}/${encodeURIComponent(audioKey)}.mp3`;
        const staticAudio = new Audio(staticUrl);
        audioRef.current = staticAudio;
        const cleanup = () => setPlaying(false);
        staticAudio.onended = cleanup;
        staticAudio.onerror = () => {
          cleanup();
          speakPatientText(fallbackText, language);
        };
        await staticAudio.play();
        return;
      } catch {
        setPlaying(false);
      }
    }
    speakPatientText(fallbackText, language);
  };

  return (
    <button
      type="button"
      onClick={handleSpeak}
      className={`${sizeClass} inline-flex flex-shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-600 shadow-sm transition-[transform,background-color] duration-200 hover:bg-sky-100 active:scale-95 ${playing ? "animate-pulse bg-sky-100" : ""} ${className}`}
      title={t("patient.audio.listen")}
      aria-label={t("patient.audio.listen")}
      data-testid={testId || `speaker-${audioKey}`}
    >
      <Volume2 className={size === "sm" ? "h-4 w-4" : "h-5 w-5"} strokeWidth={1.7} />
    </button>
  );
}